/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { TabValue } from "../config";

interface EmptyStateProps {
  tab: TabValue;
  hasDateFilter: boolean;
  hasSearchQuery: boolean;
}

const getEmptyStateConfig = (t: (key: string) => string) => ({
  pending: {
    icon: "schedule",
    title: t("reservations.empty.no_pending_requests"),
    description: t("reservations.empty.new_requests_appear"),
    descriptionWithDate: t("reservations.empty.no_requests_date"),
    descriptionWithSearch: t("reservations.empty.no_requests_search"),
  },
  declined: {
    icon: "cancel",
    title: t("reservations.empty.no_declined_requests"),
    description: t("reservations.empty.declined_appear"),
    descriptionWithDate: t("reservations.empty.no_declined_date"),
    descriptionWithSearch: t("reservations.empty.no_declined_search"),
  },
  confirmed: {
    icon: "event_available",
    title: t("reservations.empty.no_confirmed"),
    description: t("reservations.empty.confirmed_appear"),
    descriptionWithDate: t("reservations.empty.no_reservations_date"),
    descriptionWithSearch: t("reservations.empty.no_reservations_search"),
  },
  seated: {
    icon: "restaurant",
    title: t("reservations.empty.no_seated"),
    description: t("reservations.empty.seated_appear"),
    descriptionWithDate: t("reservations.empty.no_seated_date"),
    descriptionWithSearch: t("reservations.empty.no_seated_search"),
  },
  completed: {
    icon: "check_circle",
    title: t("reservations.empty.no_completed"),
    description: t("reservations.empty.completed_appear"),
    descriptionWithDate: t("reservations.empty.no_completed_date"),
    descriptionWithSearch: t("reservations.empty.no_completed_search"),
  },
});

export const EmptyState = defineComponent<EmptyStateProps>({
  name: "EmptyState",
  props: ["tab", "hasDateFilter", "hasSearchQuery"] as any,
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const EMPTY_STATE_CONFIG = getEmptyStateConfig(t);
      const config = EMPTY_STATE_CONFIG[props.tab];

      let description = config.description;
      if (props.hasSearchQuery) {
        description = config.descriptionWithSearch;
      } else if (props.hasDateFilter) {
        description = config.descriptionWithDate;
      }

      return (
        <div class="flex items-center justify-center h-96">
          <div class="text-center">
            <div class="w-20 h-20 rounded-2xl bg-gradient-to-br from-base-200 to-base-300/50 flex items-center justify-center mx-auto mb-5 shadow-inner">
              <span class="material-symbols-outlined text-base-content/40 text-4xl">
                {config.icon}
              </span>
            </div>
            <h3 class="font-semibold text-accent text-lg mb-2">
              {config.title}
            </h3>
            <p class="text-sm text-base-content/60">{description}</p>
          </div>
        </div>
      );
    };
  },
});
